import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  Package, Save, Eye, EyeOff, Clock, Rocket, Star, Plus, Trash2, X,
  Pencil, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";

const blankVariant = (productId, index) => ({
  id: `${productId || "new"}-${Date.now()}-${index}`,
  label: "",
  units: 1,
  price: 0,
  popular: false,
});

const AdminProducts = () => {
  const { authHeaders } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockEdit, setStockEdit] = useState({});
  const [variantEdits, setVariantEdits] = useState({});
  const [savingVariants, setSavingVariants] = useState({});
  const [editingDetails, setEditingDetails] = useState(null); // productId or null
  const [detailsForm, setDetailsForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("shot"); // "shot" | "box"

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/products`, { headers: authHeaders })
      .then((r) => {
        setProducts(r.data);
        const stockInit = {};
        const varInit = {};
        r.data.forEach((p) => {
          stockInit[p.id] = String(p.stock);
          varInit[p.id] = (p.variants || []).map((v) => ({ ...v }));
        });
        setStockEdit(stockInit);
        setVariantEdits(varInit);
      })
      .catch(() => toast.error("Producten laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [authHeaders]);

  const saveStock = async (id) => {
    const stock = parseInt(stockEdit[id], 10);
    if (Number.isNaN(stock) || stock < 0) {
      toast.error("Voorraad moet 0 of meer zijn");
      return;
    }
    try {
      await axios.patch(`${API}/admin/products/${id}`, { stock }, { headers: authHeaders });
      toast.success("Voorraad bijgewerkt");
      load();
    } catch {
      toast.error("Opslaan mislukt");
    }
  };

  const toggleActive = async (id, current) => {
    try {
      await axios.patch(`${API}/admin/products/${id}`, { active: !current }, { headers: authHeaders });
      toast.success(current ? "Product gedeactiveerd" : "Product geactiveerd");
      load();
    } catch {
      toast.error("Wijziging mislukt");
    }
  };

  const toggleComingSoon = async (id, current) => {
    try {
      await axios.patch(`${API}/admin/products/${id}`, { coming_soon: !current }, { headers: authHeaders });
      toast.success(current ? "Product is nu BESTELBAAR" : "Product op 'Coming Soon' gezet");
      load();
    } catch {
      toast.error("Wijziging mislukt");
    }
  };

  const updateVariantField = (productId, idx, field, value) => {
    setVariantEdits((prev) => {
      const arr = [...(prev[productId] || [])];
      const next = { ...arr[idx], [field]: value };
      arr[idx] = next;
      return { ...prev, [productId]: arr };
    });
  };

  const togglePopular = (productId, idx) => {
    setVariantEdits((prev) => {
      const arr = (prev[productId] || []).map((v, i) => ({
        ...v,
        popular: i === idx ? !v.popular : false,
      }));
      return { ...prev, [productId]: arr };
    });
  };

  const addVariant = (productId) => {
    setVariantEdits((prev) => {
      const existing = prev[productId] || [];
      return {
        ...prev,
        [productId]: [...existing, blankVariant(productId, existing.length)],
      };
    });
  };

  const removeVariant = (productId, idx) => {
    setVariantEdits((prev) => {
      const arr = [...(prev[productId] || [])];
      arr.splice(idx, 1);
      return { ...prev, [productId]: arr };
    });
  };

  const saveVariants = async (productId) => {
    const variants = variantEdits[productId] || [];
    if (variants.length === 0) {
      toast.error("Minstens 1 formaat vereist");
      return;
    }
    const seenIds = new Set();
    for (const v of variants) {
      if (!v.label || v.label.trim() === "") {
        toast.error("Label mag niet leeg zijn");
        return;
      }
      if (!(Number(v.price) > 0)) {
        toast.error(`Prijs moet > 0 zijn (${v.label || "?"})`);
        return;
      }
      if (!(parseInt(v.units, 10) >= 1)) {
        toast.error(`Aantal flesjes moet ≥ 1 zijn (${v.label || "?"})`);
        return;
      }
      if (seenIds.has(v.id)) {
        toast.error("Variant-ID's moeten uniek zijn");
        return;
      }
      seenIds.add(v.id);
    }
    setSavingVariants((s) => ({ ...s, [productId]: true }));
    try {
      const payload = {
        variants: variants.map((v) => ({
          id: v.id,
          label: v.label.trim(),
          units: parseInt(v.units, 10),
          price: Number(Number(v.price).toFixed(2)),
          popular: !!v.popular,
        })),
      };
      await axios.patch(`${API}/admin/products/${productId}`, payload, { headers: authHeaders });
      toast.success("Formaten & prijzen opgeslagen");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Opslaan mislukt";
      toast.error(typeof msg === "string" ? msg : "Opslaan mislukt");
    } finally {
      setSavingVariants((s) => ({ ...s, [productId]: false }));
    }
  };

  const startEditDetails = (p) => {
    setEditingDetails(p.id);
    setDetailsForm({
      name: p.name || "",
      tagline: p.tagline || "",
      description: p.description || "",
      image: p.image || "",
      accent_color: p.accent_color || "#ff1e1e",
      alcohol: p.alcohol || "",
      volume: p.volume || "",
      badge: p.badge || "",
      product_type: p.product_type || "shot",
    });
  };

  const saveDetails = async () => {
    try {
      await axios.patch(`${API}/admin/products/${editingDetails}`, detailsForm, {
        headers: authHeaders,
      });
      toast.success("Productdetails opgeslagen");
      setEditingDetails(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Opslaan mislukt";
      toast.error(typeof msg === "string" ? msg : "Opslaan mislukt");
    }
  };

  const deleteProduct = async (p) => {
    if (
      !window.confirm(
        `Weet je zeker dat je "${p.name}" volledig wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`
      )
    )
      return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers: authHeaders });
      toast.success(`${p.name} verwijderd`);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Verwijderen mislukt";
      toast.error(typeof msg === "string" ? msg : "Verwijderen mislukt");
    }
  };

  const handleCreate = async (form) => {
    setCreating(true);
    try {
      const variants = (form.variants || []).map((v, i) => ({
        id: v.id || `${(form.id || form.name || "new").toLowerCase().replace(/\s+/g, "-")}-${i + 1}`,
        label: v.label.trim(),
        units: parseInt(v.units, 10),
        price: Number(Number(v.price).toFixed(2)),
        popular: !!v.popular,
      }));
      if (variants.length === 0) {
        toast.error("Voeg minstens 1 formaat toe");
        setCreating(false);
        return;
      }
      const payload = {
        ...form,
        stock: parseInt(form.stock, 10) || 0,
        variants,
      };
      await axios.post(`${API}/admin/products`, payload, { headers: authHeaders });
      toast.success(`${form.name} toegevoegd!`);
      setShowCreate(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aanmaken mislukt";
      toast.error(typeof msg === "string" ? msg : "Aanmaken mislukt");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="admin-products-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-mono-x tracking-widest text-[var(--tha-red)]">
            ADMIN
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">
            PRODUCTEN & <span className="neon-red-text">FORMATEN.</span>
          </h1>
        </div>
        <button
          data-testid="admin-add-product"
          onClick={() => setShowCreate(true)}
          className="btn-red px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2"
        >
          <Plus size={14} /> NIEUW SHOTJE
        </button>
      </div>

      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package size={14} className="text-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-widest text-white">
              {tab === "shot" ? "LOSSE SHOTJES" : "SHOTDOZEN"} (
              {products.filter((p) => (p.product_type || "shot") === tab).length})
            </span>
          </div>
          <div className="flex gap-px bg-[var(--tha-border)]">
            <button
              data-testid="admin-products-tab-shot"
              onClick={() => setTab("shot")}
              className={`px-4 py-2 text-[10px] font-mono-x tracking-widest transition-colors ${
                tab === "shot"
                  ? "bg-[#111] text-[var(--tha-red)] border-b-2 border-[var(--tha-red)]"
                  : "bg-[#0a0a0a] text-[var(--tha-muted)] hover:text-white"
              }`}
            >
              SHOTJES ({products.filter((p) => (p.product_type || "shot") === "shot").length})
            </button>
            <button
              data-testid="admin-products-tab-box"
              onClick={() => setTab("box")}
              className={`px-4 py-2 text-[10px] font-mono-x tracking-widest transition-colors ${
                tab === "box"
                  ? "bg-[#111] text-[var(--tha-red)] border-b-2 border-[var(--tha-red)]"
                  : "bg-[#0a0a0a] text-[var(--tha-muted)] hover:text-white"
              }`}
            >
              DOZEN ({products.filter((p) => p.product_type === "box").length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-[var(--tha-muted)]">Laden...</div>
        ) : (
          <div className="divide-y divide-[var(--tha-border)]">
            {products
              .filter((p) => (p.product_type || "shot") === tab)
              .map((p) => (
              <ProductCard
                key={p.id}
                p={p}
                stockEdit={stockEdit}
                setStockEdit={setStockEdit}
                variantEdits={variantEdits}
                savingVariants={savingVariants}
                onSaveStock={saveStock}
                onToggleActive={toggleActive}
                onToggleComingSoon={toggleComingSoon}
                onUpdateVariantField={updateVariantField}
                onTogglePopular={togglePopular}
                onAddVariant={addVariant}
                onRemoveVariant={removeVariant}
                onSaveVariants={saveVariants}
                onStartEditDetails={startEditDetails}
                onDelete={deleteProduct}
              />
            ))}
          </div>
        )}
      </div>

      {editingDetails && (
        <DetailsModal
          form={detailsForm}
          setForm={setDetailsForm}
          onSave={saveDetails}
          onClose={() => setEditingDetails(null)}
          authHeaders={authHeaders}
        />
      )}

      {showCreate && (
        <CreateModal
          onCreate={handleCreate}
          onClose={() => setShowCreate(false)}
          creating={creating}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
};

const ProductCard = ({
  p, stockEdit, setStockEdit, variantEdits, savingVariants,
  onSaveStock, onToggleActive, onToggleComingSoon,
  onUpdateVariantField, onTogglePopular, onAddVariant, onRemoveVariant,
  onSaveVariants, onStartEditDetails, onDelete,
}) => (
  <div className="p-5 sm:p-6">
    <div className="flex flex-col sm:flex-row gap-5">
      <div className="shrink-0">
        {p.image ? (
          <img
            src={p.image}
            alt={p.name}
            className="w-24 h-24 object-cover border border-[var(--tha-border)] bg-black"
          />
        ) : (
          <div className="w-24 h-24 border border-[var(--tha-border)] bg-black flex items-center justify-center text-[var(--tha-muted)]">
            <ImageIcon size={28} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display text-2xl text-white">{p.name.toUpperCase()}</div>
            <div className="text-xs font-mono-x text-[var(--tha-muted)] mt-1">
              {p.alcohol} · {p.volume} · #{p.id}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              data-testid={`edit-details-${p.id}`}
              onClick={() => onStartEditDetails(p)}
              className="btn-ghost-red px-3 py-2 text-xs inline-flex items-center gap-1"
              title="Bewerk naam, beschrijving, afbeelding"
            >
              <Pencil size={12} /> BEWERK
            </button>
            <button
              data-testid={`toggle-coming-soon-${p.id}`}
              onClick={() => onToggleComingSoon(p.id, p.coming_soon)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono-x tracking-widest border ${
                p.coming_soon
                  ? "border-[var(--tha-red)] text-[var(--tha-red)] bg-[#1a0303]"
                  : "border-[#22c55e] text-[#22c55e]"
              }`}
            >
              {p.coming_soon ? <Clock size={12} /> : <Rocket size={12} />}
              {p.coming_soon ? "COMING SOON" : "BESTELBAAR"}
            </button>
            <button
              data-testid={`toggle-active-${p.id}`}
              onClick={() => onToggleActive(p.id, p.active)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono-x tracking-widest border ${
                p.active
                  ? "border-[#22c55e] text-[#22c55e]"
                  : "border-[var(--tha-muted)] text-[var(--tha-muted)]"
              }`}
            >
              {p.active ? <Eye size={12} /> : <EyeOff size={12} />}
              {p.active ? "ZICHTBAAR" : "VERBORGEN"}
            </button>
            <button
              data-testid={`delete-product-${p.id}`}
              onClick={() => onDelete(p)}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs font-mono-x tracking-widest border border-[var(--tha-muted)] text-[var(--tha-muted)] hover:border-[var(--tha-red)] hover:text-[var(--tha-red)]"
              title="Verwijderen (alleen mogelijk als geen bestellingen)"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        {p.tagline && (
          <p className="mt-2 text-sm text-[var(--tha-muted)] italic">{p.tagline}</p>
        )}

        {/* Variants editor */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              FORMATEN & PRIJZEN
            </div>
            <button
              data-testid={`add-variant-${p.id}`}
              onClick={() => onAddVariant(p.id)}
              className="text-xs px-3 py-1.5 inline-flex items-center gap-1 border border-[var(--tha-border)] hover:border-[var(--tha-red)] hover:text-[var(--tha-red)] text-[var(--tha-muted)] font-mono-x tracking-widest"
            >
              <Plus size={11} /> VOEG FORMAAT TOE
            </button>
          </div>
          <div className="space-y-2">
            {(variantEdits[p.id] || []).map((v, idx) => {
              const perUnit = v.units > 0 ? (Number(v.price) / Number(v.units)).toFixed(2) : "—";
              return (
                <div
                  key={v.id}
                  data-testid={`variant-row-${v.id}`}
                  className="grid grid-cols-12 gap-2 items-end border border-[var(--tha-border)] p-3 bg-[#070707]"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      LABEL
                    </label>
                    <input
                      data-testid={`variant-label-${v.id}`}
                      value={v.label}
                      placeholder='Bijv. "10-pack — 20% korting"'
                      onChange={(e) => onUpdateVariantField(p.id, idx, "label", e.target.value)}
                      className="block mt-1 w-full px-2 py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      # FLESJES
                    </label>
                    <input
                      data-testid={`variant-units-${v.id}`}
                      type="number"
                      min="1"
                      value={v.units}
                      onChange={(e) => onUpdateVariantField(p.id, idx, "units", e.target.value)}
                      className="block mt-1 w-full px-2 py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      PRIJS (€)
                    </label>
                    <input
                      data-testid={`variant-price-${v.id}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={v.price}
                      onChange={(e) => onUpdateVariantField(p.id, idx, "price", e.target.value)}
                      className="block mt-1 w-full px-2 py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      P/SHOT
                    </label>
                    <div className="mt-1 px-2 py-2 text-sm text-[var(--tha-muted)] font-mono-x">
                      € {perUnit}
                    </div>
                  </div>
                  <div className="col-span-6 sm:col-span-1 flex sm:justify-center">
                    <button
                      type="button"
                      data-testid={`variant-popular-${v.id}`}
                      onClick={() => onTogglePopular(p.id, idx)}
                      title="Markeer als populair"
                      className={`inline-flex items-center justify-center w-10 h-10 border transition-colors ${
                        v.popular
                          ? "border-[var(--tha-red)] bg-[var(--tha-red)] text-black"
                          : "border-[var(--tha-border)] text-[var(--tha-muted)] hover:text-white"
                      }`}
                    >
                      <Star size={14} fill={v.popular ? "currentColor" : "none"} />
                    </button>
                  </div>
                  <div className="col-span-6 sm:col-span-1 flex sm:justify-center">
                    <button
                      type="button"
                      data-testid={`variant-remove-${v.id}`}
                      onClick={() => onRemoveVariant(p.id, idx)}
                      title="Verwijder dit formaat"
                      className="inline-flex items-center justify-center w-10 h-10 border border-[var(--tha-border)] text-[var(--tha-muted)] hover:border-[var(--tha-red)] hover:text-[var(--tha-red)]"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              data-testid={`save-variants-${p.id}`}
              onClick={() => onSaveVariants(p.id)}
              disabled={!!savingVariants[p.id]}
              className="btn-red px-5 py-2.5 inline-flex items-center gap-2 font-display tracking-wider text-sm disabled:opacity-60"
            >
              <Save size={14} /> {savingVariants[p.id] ? "OPSLAAN..." : "OPSLAAN"}
            </button>
            <p className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              TIP: HOU "% KORTING" IN HET LABEL ≤ 25% (NL ALCOHOLWET)
            </p>
          </div>
        </div>

        {/* Stock */}
        <div className="mt-6 flex flex-wrap items-end gap-3 border-t border-[var(--tha-border)] pt-5">
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              VOORRAAD (FLESJES)
            </label>
            <input
              data-testid={`stock-input-${p.id}`}
              type="number"
              min="0"
              value={stockEdit[p.id] ?? ""}
              onChange={(e) => setStockEdit((s) => ({ ...s, [p.id]: e.target.value }))}
              className="block mt-1 w-40 px-3 py-2 text-sm"
            />
          </div>
          <button
            data-testid={`save-stock-${p.id}`}
            onClick={() => onSaveStock(p.id)}
            className="btn-red px-5 py-2.5 inline-flex items-center gap-2 font-display tracking-wider text-sm"
          >
            <Save size={14} /> OPSLAAN
          </button>
          <div className="ml-auto text-xs text-[var(--tha-muted)] font-mono-x">
            HUIDIG: {p.stock} · GERESERVEERD: {p.reserved || 0}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Field = ({ label, value, onChange, placeholder, type = "text", textarea, rows = 3 }) => (
  <label className="block">
    <span className="block text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-1.5">
      {label.toUpperCase()}
    </span>
    {textarea ? (
      <textarea
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-[#070707] border border-[var(--tha-border)] text-sm text-white focus:outline-none focus:border-[var(--tha-red)] resize-y"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-[#070707] border border-[var(--tha-border)] text-sm text-white focus:outline-none focus:border-[var(--tha-red)]"
      />
    )}
  </label>
);

const ModalShell = ({ title, onClose, children }) => (
  <div
    className="fixed inset-0 z-[200] bg-black/80 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
    onClick={onClose}
  >
    <div
      className="w-full max-w-3xl bg-[#0a0a0a] border border-[var(--tha-border)] my-8"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-6 py-4 border-b border-[var(--tha-border)] flex items-center justify-between">
        <div className="font-display text-xl text-white">{title}</div>
        <button onClick={onClose} className="text-[var(--tha-muted)] hover:text-white">
          <X size={18} />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const DetailsModal = ({ form, setForm, onSave, onClose, authHeaders }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <ModalShell title="BEWERK PRODUCTGEGEVENS" onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Naam" value={form.name} onChange={set("name")} />
        <Field label="Tagline" value={form.tagline} onChange={set("tagline")} placeholder="Korte oneliner" />
        <div className="md:col-span-2">
          <Field label="Beschrijving" textarea rows={4} value={form.description} onChange={set("description")} />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-2">
            PRODUCTFOTO
          </label>
          <ImageUpload
            value={form.image}
            onChange={(url) => setForm((f) => ({ ...f, image: url }))}
            authHeaders={authHeaders}
            testId="product-image-upload-edit"
          />
        </div>
        <Field label="Accent-kleur (hex)" value={form.accent_color} onChange={set("accent_color")} placeholder="#ff1e1e" />
        <div className="flex items-center gap-3">
          <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">PREVIEW</div>
          <div className="w-8 h-8 border border-[var(--tha-border)]" style={{ background: form.accent_color || "#ff1e1e" }} />
        </div>
        <Field label="Alcohol" value={form.alcohol} onChange={set("alcohol")} placeholder="14,5% vol" />
        <Field label="Volume" value={form.volume} onChange={set("volume")} placeholder="20 ml" />
        <Field label="Badge op foto (optioneel)" value={form.badge || ""} onChange={set("badge")} placeholder='Bijv. "24 SHOTS"' />
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">
            CATEGORIE
          </label>
          <select
            value={form.product_type || "shot"}
            onChange={set("product_type")}
            data-testid="product-type-select-edit"
            className="w-full bg-[#050505] border border-[var(--tha-border)] px-3 py-2 text-sm font-mono-x"
          >
            <option value="shot">Los shotje</option>
            <option value="box">Shotdoos / variatiepakket</option>
          </select>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button data-testid="save-product-details" onClick={onSave} className="btn-red px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2">
          <Save size={14} /> OPSLAAN
        </button>
        <button onClick={onClose} className="btn-ghost-red px-5 py-3 font-display tracking-wider text-sm">
          ANNULEREN
        </button>
      </div>
    </ModalShell>
  );
};

const CreateModal = ({ onCreate, onClose, creating, authHeaders }) => {
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    description: "",
    image: "",
    accent_color: "#ff1e1e",
    alcohol: "14,5% vol",
    volume: "20 ml",
    stock: 200,
    coming_soon: true,
    revealed: true,
    product_type: "shot",
    badge: "",
    variants: [
      { id: "", label: "6-pack", units: 6, price: 21.95, popular: false },
      { id: "", label: "12-pack — beste deal", units: 12, price: 41.95, popular: true },
      { id: "", label: "24-pack", units: 24, price: 79.95, popular: false },
      { id: "", label: "48-pack — party", units: 48, price: 154.95, popular: false },
    ],
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateV = (i, k, v) =>
    setForm((f) => {
      const arr = [...f.variants];
      arr[i] = { ...arr[i], [k]: v };
      return { ...f, variants: arr };
    });
  const addV = () =>
    setForm((f) => ({
      ...f,
      variants: [...f.variants, { id: "", label: "", units: 1, price: 0, popular: false }],
    }));
  const removeV = (i) =>
    setForm((f) => {
      const arr = [...f.variants];
      arr.splice(i, 1);
      return { ...f, variants: arr };
    });

  return (
    <ModalShell title="NIEUW SHOTJE TOEVOEGEN" onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Naam *" value={form.name} onChange={set("name")} placeholder='Bijv. "Lava Bomb"' />
        <Field label="Tagline" value={form.tagline} onChange={set("tagline")} placeholder="Korte oneliner" />
        <div className="md:col-span-2">
          <Field label="Beschrijving" textarea rows={3} value={form.description} onChange={set("description")} />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-2">
            PRODUCTFOTO *
          </label>
          <ImageUpload
            value={form.image}
            onChange={(url) => setForm((f) => ({ ...f, image: url }))}
            authHeaders={authHeaders}
            testId="product-image-upload-create"
          />
        </div>
        <Field label="Accent-kleur (hex)" value={form.accent_color} onChange={set("accent_color")} />
        <Field label="Alcohol" value={form.alcohol} onChange={set("alcohol")} />
        <Field label="Volume" value={form.volume} onChange={set("volume")} />
        <Field label="Voorraad (start)" type="number" value={form.stock} onChange={set("stock")} />
        <Field label="Badge op foto (optioneel)" value={form.badge} onChange={set("badge")} placeholder='Bijv. "24 SHOTS"' />
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">
            CATEGORIE *
          </label>
          <select
            value={form.product_type}
            onChange={set("product_type")}
            data-testid="product-type-select-create"
            className="w-full bg-[#050505] border border-[var(--tha-border)] px-3 py-2 text-sm font-mono-x"
          >
            <option value="shot">Los shotje (smaak)</option>
            <option value="box">Shotdoos / variatiepakket</option>
          </select>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
            FORMATEN & PRIJZEN ({form.variants.length})
          </div>
          <button
            data-testid="create-add-variant"
            onClick={addV}
            type="button"
            className="text-xs px-3 py-1.5 inline-flex items-center gap-1 border border-[var(--tha-border)] hover:border-[var(--tha-red)] hover:text-[var(--tha-red)] text-[var(--tha-muted)] font-mono-x tracking-widest"
          >
            <Plus size={11} /> VOEG TOE
          </button>
        </div>
        <div className="space-y-2">
          {form.variants.map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border border-[var(--tha-border)] p-3 bg-[#070707]">
              <div className="col-span-12 sm:col-span-5">
                <Field label="Label" value={v.label} onChange={(e) => updateV(i, "label", e.target.value)} />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Field label="# flesjes" type="number" value={v.units} onChange={(e) => updateV(i, "units", e.target.value)} />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Field label="Prijs (€)" type="number" value={v.price} onChange={(e) => updateV(i, "price", e.target.value)} />
              </div>
              <div className="col-span-6 sm:col-span-2 flex">
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      variants: f.variants.map((x, j) => ({ ...x, popular: j === i ? !x.popular : false })),
                    }))
                  }
                  className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-mono-x tracking-widest border ${
                    v.popular
                      ? "border-[var(--tha-red)] bg-[var(--tha-red)] text-black"
                      : "border-[var(--tha-border)] text-[var(--tha-muted)]"
                  }`}
                >
                  <Star size={11} fill={v.popular ? "currentColor" : "none"} /> POP
                </button>
              </div>
              <div className="col-span-6 sm:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeV(i)}
                  className="inline-flex items-center justify-center w-10 h-10 border border-[var(--tha-border)] text-[var(--tha-muted)] hover:text-[var(--tha-red)] hover:border-[var(--tha-red)]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex gap-3 flex-wrap">
        <button
          data-testid="create-product-submit"
          onClick={() => onCreate(form)}
          disabled={creating || !form.name.trim()}
          className="btn-red px-5 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Plus size={14} /> {creating ? "AANMAKEN..." : "SHOTJE AANMAKEN"}
        </button>
        <button onClick={onClose} className="btn-ghost-red px-5 py-3 font-display tracking-wider text-sm">
          ANNULEREN
        </button>
        <p className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] self-center">
          Start hem in "Coming Soon"-modus. Zet hem op bestelbaar wanneer je klaar bent.
        </p>
      </div>
    </ModalShell>
  );
};

export default AdminProducts;
